import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import PageHero from "./PageHero";

// Image Assets
import bgHeroImg from "../assets/services.jpg";
import serviceImg1 from "../assets/modernAgriculture.jpg";      // Modern Agricultural Skills
import serviceImg2 from "../assets/afforestation.jpg";       // Climate Resilience
import serviceImg3 from "../assets/youth.jpg";    // Education & Skills Training
import serviceImg4 from "../assets/povertyAlleviation.jpg";    // Poverty Alleviation
import serviceImg5 from "../assets/orphans.jpg";  // Empowering Vulnerable Groups
import serviceImg6 from "../assets/communityWork.jpg";   // Community Capacity Building

// React Icons
import { 
    FaSeedling, 
    FaCloudSunRain, 
    FaGraduationCap, 
    FaChartLine, 
    FaUsers, 
    FaHandsHelping,
    FaCheck
} from "react-icons/fa";

export default function ServicesBanner(props) {
    const appContext = useContext(AppContext);
    const language = appContext.language || "sw";

    const [ready, setReady] = useState(false);

    // MWADEO 6 Core Services matching Home Services
    const mwadeoServices = [
        {
            id: "01",
            icon: <FaSeedling size={26} />,
            image: serviceImg1,
            title: {
                en: "Modern Agricultural Skills",
                sw: "Ujuzi wa Kilimo cha Kisasa"
            },
            desc: {
                en: "Training smallholder farmers in modern, sustainable techniques to boost productivity and farm yields amidst changing climates.",
                sw: "Kuwapatia mafunzo wakulima wadogo kuhusu mbinu za kisasa na endelevu ili kuongeza tija na mavuno katikati ya mabadiliko ya nchi."
            },
            highlights: [
                { en: "Sustainable Farm Techniques", sw: "Mbinu Endelevu za Kilimo" },
                { en: "Crop Yield Maximization", sw: "Kukuza Tija na Mavuno" }
            ]
        },
        {
            id: "02",
            icon: <FaCloudSunRain size={26} />,
            image: serviceImg2,
            title: {
                en: "Climate Resilience",
                sw: "Ustahimilivu wa Hali ya Hewa"
            },
            desc: {
                en: "Promoting sustainable environmental actions and climate adaptation strategies to protect vulnerable communities.",
                sw: "Kuhamasisha vitendo vya kuhifadhi mazingira na mikakati ya kukabiliana na mabadiliko ya tabianchi ili kulinda jamii."
            },
            highlights: [
                { en: "Environmental Conservation", sw: "Hifadhi ya Mazingira" },
                { en: "Climate Adaptation Strategies", sw: "Mikakati ya Kukabiliana na Hali ya Hewa" }
            ]
        },
        {
            id: "03",
            icon: <FaGraduationCap size={26} />,
            image: serviceImg3,
            title: {
                en: "Education & Skills Training",
                sw: "Elimu na Mafunzo ya Ujuzi"
            },
            desc: {
                en: "Equipping community members with practical vocational and working skills to enable long-term self-reliance.",
                sw: "Kuwapatia wanajamii mafunzo ya vitendo na ujuzi wa kazi ili kuwawezesha kujitegemea kwa muda mrefu."
            },
            highlights: [
                { en: "Vocational Skill Building", sw: "Kujenga Ujuzi wa Ufundi" },
                { en: "Practical Career Training", sw: "Elimu ya Kazi na Vitendo" }
            ]
        },
        {
            id: "04",
            icon: <FaChartLine size={26} />,
            image: serviceImg4,
            title: {
                en: "Poverty Alleviation",
                sw: "Kupunguza Umaskini"
            },
            desc: {
                en: "Fostering sustainable economic opportunities to elevate living standards and ensure a productive community.",
                sw: "Kutengeneza fursa za kiuchumi zilizo endelevu ili kuinua kiwango cha maisha na kujenga jamii yenye tija."
            },
            highlights: [
                { en: "Economic Opportunity Creation", sw: "Kutengeneza Fursa za Kiuchumi" },
                { en: "Standard of Living Elevation", sw: "Kuinua Viwango vya Maisha" }
            ]
        },
        {
            id: "05",
            icon: <FaUsers size={26} />,
            image: serviceImg5,
            title: {
                en: "Empowering Vulnerable Groups",
                sw: "Kuwezesha Makundi Yaliyo Hatarini"
            },
            desc: {
                en: "Targeted support programs dedicated to empowering women, youth, and underprivileged groups for inclusive growth.",
                sw: "Mipango maalum inayolenga kuwainua wanawake, vijana, na makundi yaliyopo kwenye mazingira magumu kwa maendeleo jumuishi."
            },
            highlights: [
                { en: "Women & Youth Empowerment", sw: "Uwezeshaji wa Wanawake na Vijana" },
                { en: "Inclusive Social Support", sw: "Msaada Jumuishi wa Kijamii" }
            ]
        },
        {
            id: "06",
            icon: <FaHandsHelping size={26} />,
            image: serviceImg6,
            title: {
                en: "Community Capacity Building",
                sw: "Ujenzi wa Uwezo wa Jamii"
            },
            desc: {
                en: "Mobilizing local action, leadership, and resources to solve community development challenges collaboratively.",
                sw: "Kuhamasisha rasilimali za ndani, uongozi, na ushirikiano wa jamii katika kutatua changamoto za maendeleo."
            },
            highlights: [
                { en: "Local Leadership Action", sw: "Uongozi na Hamasa ya Ndani" },
                { en: "Collaborative Problem Solving", sw: "Ushirikiano wa Utatuzi wa Changamoto" }
            ]
        }
    ];

    useEffect(() => {
        setReady(true);
    }, []);

    if (!ready) {
        return (
            <div className="container mSupportLoading d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                {getInlineLoader()}
            </div>
        );
    }

    return (
        <div className="mwadeo-services-container">
            {/* 1. PageHero Component (Unchanged) */}
            <PageHero 
                kicker={language === "sw" ? "HUDUMA NA PROGRAMU ZETU" : "OUR SERVICES & PROGRAMS"} 
                title={language === "sw" ? "Kuinua Jamii Kupitia Mipango Endelevu" : "Empowering Communities Through Sustainable Programs"} 
                bgImage={serviceImg4} 
            />

            {/* 2. Intro Sub-Section */}
            <section className="mwadeo-srv-intro py-5 border-bottom bg-white">
                <div className="container py-2 text-center max-w-700 mx-auto">
                    <span className="badge bg-primary text-white text-uppercase letter-spacing-2 px-3 py-2 mb-3">
                        {language === "sw" ? "Afua za MWADEO" : "MWADEO Interventions"}
                    </span>
                    <h2 className="title-font-bold text-dark text-uppercase display-6 mb-3">
                        {language === "sw" ? "Huduma na Mipango Yenye Tija" : "Services & Programs for Community Growth"}
                    </h2>
                    <p className="font-regular text-muted lh-lg mb-0">
                        {language === "sw"
                            ? "Kipengele hiki kinaonyesha huduma kuu sita tunazozitoa kulingana na dira na azimio la MWADEO katika kuinua hali ya maisha ya Mwananchi nchini Tanzania."
                            : "Explore our six core thematic interventions structured to tackle poverty, strengthen climate adaptation, and uplift livelihoods across Tanzania."}
                    </p>
                </div>
            </section>

            {/* 3. Services Grid Section */}
            <section className="mwadeo-srv-grid-section py-5 bg-light">
                <div className="container py-4">
                    <div className="row g-4">
                        {mwadeoServices.map((service, idx) => (
                            <div className="col-12 col-lg-6" key={idx}>
                                <div className="mwadeo-srv-card h-100 bg-white rounded-4 border overflow-hidden shadow-sm d-flex flex-column justify-content-between">
                                    
                                    {/* Top Image Banner */}
                                    <div className="mwadeo-srv-card-media position-relative" style={{ backgroundImage: `url(${service.image})` }}>
                                        <div className="mwadeo-srv-media-overlay position-absolute top-0 start-0 w-100 h-100"></div>
                                        
                                        <div className="position-absolute top-0 start-0 p-3 w-100 d-flex justify-content-between align-items-center z-2">
                                            <span className="mwadeo-srv-number title-font-bold text-white px-3 py-1 rounded-pill">
                                                {service.id}
                                            </span>
                                            <div className="mwadeo-srv-icon-bubble bg-white primaryColor-text shadow-sm rounded-circle d-flex align-items-center justify-content-center">
                                                {service.icon}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Content */}
                                    <div className="mwadeo-srv-card-body p-4 p-md-5 d-flex flex-column flex-grow-1 justify-content-between">
                                        <div>
                                            <h3 className="title-font-bold text-dark mb-3">
                                                {service.title[language]}
                                            </h3>
                                            <p className="font-regular mwadeo-srv-desc lh-lg mb-4">
                                                {service.desc[language]}
                                            </p>
                                        </div>

                                        {/* Key Highlights */}
                                        <div className="mwadeo-srv-highlights pt-3 border-top mt-auto">
                                            <div className="row g-2">
                                                {service.highlights.map((h, i) => (
                                                    <div className="col-12 col-sm-6" key={i}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="mwadeo-srv-check-icon rounded-circle d-flex align-items-center justify-content-center">
                                                                <FaCheck size={10} />
                                                            </div>
                                                            <span className="font-regular small text-secondary-dark fw-semibold">
                                                                {h[language]}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}